/**
 * Produccion: cocinar lo planeado y descontarlo del inventario (Req. 13).
 *
 * ESTE ES EL PUNTO DONDE EL COSTO SE CONGELA.
 *
 * Las recetas calculan su costo al momento, con los precios vigentes. La
 * produccion NO: al completarla se guarda el costo del dia en `Production` y en
 * `ProductionItem`, y nunca se vuelve a calcular. Es lo que hace que comprar
 * mas caro el mes que entra no reescriba las ganancias de este mes (Req. 13.6).
 *
 * Completar una produccion hace CUATRO cosas en UNA sola transaccion:
 *   1. descuenta las cantidades del inventario
 *   2. escribe un movimiento por insumo en el libro
 *   3. guarda el costo congelado de cada renglon
 *   4. marca la produccion como completada
 *
 * Si cualquiera falla, no se hace ninguna: el inventario no puede quedar
 * descontado sin registro, ni la produccion completada sin descontar.
 */

import { db } from '../schema';
import { enEscritura } from '../tx';
import { nuevoId } from '../ids';
import { exigirCantidadPositiva } from '../validation';
import { ErrorValidacion } from '@/domain/errors';
import { ahoraIso, hoy, type FechaLocal } from '@/domain/dates';
import { escalarItems } from '@/domain/recipes';
import { costoPorBase, aplicarSalida, aplicarEntradaValuada } from '@/domain/inventory';
import { formatearCantidadBase } from '@/domain/units';
import type { Centavos } from '@/domain/money';
import type {
  Ingredient,
  PlannedItem,
  Production,
  ProductionItem,
  Recipe,
} from '../types';
import { estadoDe, construirMovimiento } from './ingredients';
import { agendaDelDia, type AgendaDelDia } from '@/domain/planning';

// ---------------------------------------------------------------------------
// Lo que hay que cocinar
// ---------------------------------------------------------------------------

export interface RenglonIngrediente {
  ingredientId: string;
  nombreInsumo: string;
  /** Cantidad escalada a las porciones a producir. */
  cantidadBase: number;
  cantidadLegible: string;
  /** Existencia actual, para comparar. */
  disponibleBase: number;
  disponibleLegible: string;
  /** true cuando el inventario no alcanza (Req. 13.9). */
  insuficiente: boolean;
  faltanteLegible: string | null;
  costoCentavos: Centavos | null;
  sinCosto: boolean;
}

export interface TareaProduccion {
  platillo: PlannedItem;
  nombreReceta: string;
  porcionesAProducir: number;
  ingredientes: RenglonIngrediente[];
  costoTotalCentavos: Centavos;
  /** true si a algun insumo le falta precio: el costo esta incompleto. */
  costoIncompleto: boolean;
  nombresSinCosto: string[];
  /** true si a algun insumo no le alcanza la existencia. */
  faltaInventario: boolean;
  produccion: Production | null;
  yaCompletada: boolean;
}

/**
 * Arma la tarea de cocina de un platillo: cuanto de cada insumo y si alcanza.
 *
 * `porciones` permite ajustar antes de confirmar, por si se decide hacer mas o
 * menos de lo planeado.
 */
export async function verTarea(
  plannedItemId: string,
  porciones?: number,
): Promise<TareaProduccion | undefined> {
  const platillo = await db.plannedItems.get(plannedItemId);
  if (platillo === undefined) return undefined;

  const receta = await db.recipes.get(platillo.recipeId);
  const produccion = await produccionVigenteDe(plannedItemId);

  const porcionesAProducir = porciones ?? platillo.porcionesPlaneadas;

  if (receta === undefined) {
    return {
      platillo,
      nombreReceta: '(receta eliminada)',
      porcionesAProducir,
      ingredientes: [],
      costoTotalCentavos: 0,
      costoIncompleto: true,
      nombresSinCosto: [],
      faltaInventario: false,
      produccion,
      yaCompletada: produccion?.estado === 'completada',
    };
  }

  const items = await db.recipeItems.where('recipeId').equals(receta.id).toArray();
  const insumos = new Map(
    (await db.ingredients.bulkGet(items.map((i) => i.ingredientId)))
      .filter((i): i is Ingredient => i !== undefined)
      .map((i) => [i.id, i]),
  );

  const escalados = escalarItems(
    items.map((i) => ({
      ingredientId: i.ingredientId,
      cantidadBase: i.cantidadBase,
      escala: i.escala,
    })),
    porcionesAProducir,
    receta.rendimientoPorciones,
  );

  const ingredientes: RenglonIngrediente[] = escalados.map(({ item, cantidadBase }) => {
    const insumo = insumos.get(item.ingredientId);

    if (insumo === undefined) {
      return {
        ingredientId: item.ingredientId,
        nombreInsumo: '(insumo eliminado)',
        cantidadBase,
        cantidadLegible: String(cantidadBase),
        disponibleBase: 0,
        disponibleLegible: '0',
        insuficiente: true,
        faltanteLegible: null,
        costoCentavos: null,
        sinCosto: true,
      };
    }

    const { costoPorBase: unitario } = costoPorBase(estadoDe(insumo));
    const faltante = Math.max(0, cantidadBase - insumo.stockBase);

    return {
      ingredientId: insumo.id,
      nombreInsumo: insumo.nombre,
      cantidadBase,
      cantidadLegible: legible(cantidadBase, insumo),
      disponibleBase: insumo.stockBase,
      disponibleLegible: legible(insumo.stockBase, insumo),
      insuficiente: faltante > 0,
      faltanteLegible: faltante > 0 ? legible(faltante, insumo) : null,
      costoCentavos: unitario === null ? null : Math.round(cantidadBase * unitario),
      sinCosto: unitario === null,
    };
  });

  const nombresSinCosto = ingredientes.filter((i) => i.sinCosto).map((i) => i.nombreInsumo);

  return {
    platillo,
    nombreReceta: receta.nombre,
    porcionesAProducir,
    ingredientes,
    costoTotalCentavos: ingredientes.reduce((s, i) => s + (i.costoCentavos ?? 0), 0),
    costoIncompleto: nombresSinCosto.length > 0,
    nombresSinCosto,
    faltaInventario: ingredientes.some((i) => i.insuficiente),
    produccion,
    yaCompletada: produccion?.estado === 'completada',
  };
}

function legible(cantidadBase: number, insumo: Ingredient): string {
  return formatearCantidadBase(cantidadBase, insumo.dimension, insumo.unidadCompraPreferida);
}

/** La produccion que manda para un platillo: la no cancelada mas reciente. */
async function produccionVigenteDe(plannedItemId: string): Promise<Production | null> {
  const todas = await db.productions.where('plannedItemId').equals(plannedItemId).toArray();
  const vigentes = todas.filter((p) => p.estado !== 'cancelada');
  return vigentes[vigentes.length - 1] ?? todas[todas.length - 1] ?? null;
}

// ---------------------------------------------------------------------------
// La agenda del dia
// ---------------------------------------------------------------------------

export interface AgendaConDetalle {
  fecha: FechaLocal;
  entregasDeHoy: TareaProduccion[];
  prepararHoy: TareaProduccion[];
  atrasados: TareaProduccion[];
}

/**
 * Que se entrega hoy, que se prepara hoy y que quedo atrasado.
 *
 * Se consulta una ventana amplia de fechas y el dominio decide la clasificacion,
 * para que la regla viva en un solo lugar y este cubierta por pruebas puras.
 */
export async function verAgenda(fecha: FechaLocal = hoy()): Promise<AgendaConDetalle> {
  const platillos = await db.plannedItems.toArray();

  const completados = new Set(
    (await db.productions.toArray())
      .filter((p) => p.estado === 'completada')
      .map((p) => p.plannedItemId),
  );

  const agenda: AgendaDelDia<PlannedItem> = agendaDelDia(platillos, fecha, (item) =>
    completados.has(item.id),
  );

  const detallar = async (items: PlannedItem[]): Promise<TareaProduccion[]> => {
    const tareas: TareaProduccion[] = [];
    for (const item of items) {
      const tarea = await verTarea(item.id);
      if (tarea !== undefined) tareas.push(tarea);
    }
    return tareas;
  };

  return {
    fecha,
    entregasDeHoy: await detallar(agenda.entregasDeHoy),
    prepararHoy: await detallar(agenda.prepararHoy),
    atrasados: await detallar(agenda.atrasados),
  };
}

// ---------------------------------------------------------------------------
// Completar la produccion
// ---------------------------------------------------------------------------

export interface AjusteCantidad {
  ingredientId: string;
  /** Cantidad realmente usada, en unidad base (Req. 13.8). */
  cantidadRealBase: number;
}

export interface ResultadoProduccion {
  produccion: Production;
  costoTotalCentavos: Centavos;
  advertencias: string[];
}

/**
 * Marca una produccion como completada y descuenta el inventario.
 *
 * `ajustes` permite corregir las cantidades realmente usadas antes de confirmar:
 * la receta dice 2 kg pero el paquete traia 2.1 y se usaron todos.
 *
 * La salida se permite aunque el inventario no alcance, y se advierte. Bloquear
 * dejaria a la usuaria atorada con la comida ya hecha.
 */
export async function completarProduccion(
  plannedItemId: string,
  opciones: { porciones?: number; ajustes?: readonly AjusteCantidad[]; fecha?: FechaLocal } = {},
): Promise<ResultadoProduccion> {
  const platillo = await db.plannedItems.get(plannedItemId);
  if (platillo === undefined) {
    throw new ErrorValidacion('No se encontro el platillo del menu.');
  }

  const yaHecha = await produccionVigenteDe(plannedItemId);
  if (yaHecha?.estado === 'completada') {
    throw new ErrorValidacion(
      `"${platillo.fechaVenta}" ya tiene esta produccion completada. ` +
        'Si fue un error, reviértela en lugar de completarla otra vez.',
    );
  }

  const porciones = exigirCantidadPositiva(
    opciones.porciones ?? platillo.porcionesPlaneadas,
    'porciones',
  );

  const tarea = await verTarea(plannedItemId, porciones);
  if (tarea === undefined) {
    throw new ErrorValidacion('No se pudo armar la produccion.');
  }
  if (tarea.ingredientes.length === 0) {
    throw new ErrorValidacion(
      'Esta receta no tiene ingredientes, asi que no hay nada que descontar del inventario.',
    );
  }

  const ajustes = new Map((opciones.ajustes ?? []).map((a) => [a.ingredientId, a.cantidadRealBase]));
  const fecha = opciones.fecha ?? hoy();
  const advertencias: string[] = [];

  // Se resuelve TODO antes de abrir la transaccion: si algo esta mal, no se
  // alcanza a escribir nada.
  const insumos = new Map(
    (await db.ingredients.bulkGet(tarea.ingredientes.map((i) => i.ingredientId)))
      .filter((i): i is Ingredient => i !== undefined)
      .map((i) => [i.id, i]),
  );

  const productionId = nuevoId('prod');
  const renglones: ProductionItem[] = [];
  const movimientos: ReturnType<typeof construirMovimiento>[] = [];
  const nuevosEstados: Array<{ id: string; estado: ReturnType<typeof estadoDe> }> = [];

  let costoTotal = 0;
  let algunImputado = false;

  for (const renglon of tarea.ingredientes) {
    const insumo = insumos.get(renglon.ingredientId);
    if (insumo === undefined) continue;

    const cantidadReal = ajustes.get(renglon.ingredientId) ?? renglon.cantidadBase;
    if (!Number.isFinite(cantidadReal) || cantidadReal < 0) {
      throw new ErrorValidacion(`La cantidad usada de "${insumo.nombre}" no es valida.`);
    }
    if (cantidadReal === 0) continue;

    const { costoPorBase: unitarioAntes } = costoPorBase(estadoDe(insumo));
    const resultado = aplicarSalida(estadoDe(insumo), cantidadReal);

    advertencias.push(...resultado.advertencias.map((a) => `${insumo.nombre}: ${a}`));

    const costoRenglon = resultado.deltas.costoAplicadoCentavos ?? 0;
    costoTotal += costoRenglon;
    if (resultado.deltas.costoImputado) algunImputado = true;

    nuevosEstados.push({ id: insumo.id, estado: resultado.estado });

    movimientos.push(
      construirMovimiento({
        ingredientId: insumo.id,
        tipo: 'consumo_produccion',
        fecha,
        deltas: resultado.deltas,
        refTipo: 'production',
        refId: productionId,
        motivo: `${tarea.nombreReceta}, ${porciones} porciones`,
      }),
    );

    renglones.push({
      id: nuevoId('pitem'),
      productionId,
      ingredientId: insumo.id,
      cantidadPlanBase: renglon.cantidadBase,
      cantidadRealBase: cantidadReal,
      costoCentavos: costoRenglon,
      costoPorBaseSnapshot: unitarioAntes ?? 0,
      costoImputado: resultado.deltas.costoImputado,
    });
  }

  const produccion: Production = {
    id: productionId,
    plannedItemId,
    fechaPrep: platillo.fechaPrep,
    fechaVenta: platillo.fechaVenta,
    recipeId: platillo.recipeId,
    porcionesAProducir: porciones,
    estado: 'completada',
    completadaEn: ahoraIso(),
    costoTotalCentavos: costoTotal,
    costoEstimado: algunImputado,
    costoIncompleto: tarea.costoIncompleto,
    revertidaPor: null,
    creadoEn: ahoraIso(),
  };

  await enEscritura(
    [db.productions, db.productionItems, db.ingredients, db.inventoryMovements],
    async () => {
      await db.productions.add(produccion);
      await db.productionItems.bulkAdd(renglones);
      await db.inventoryMovements.bulkAdd(movimientos);
      for (const { id, estado } of nuevosEstados) {
        await db.ingredients.update(id, estado);
      }
    },
  );

  return { produccion, costoTotalCentavos: costoTotal, advertencias };
}

// ---------------------------------------------------------------------------
// Revertir
// ---------------------------------------------------------------------------

/**
 * Deshace una produccion completada devolviendo los insumos al inventario.
 *
 * NO borra nada: escribe movimientos compensatorios y marca la produccion
 * original como revertida (Req. 13.12). El libro debe poder explicar cada
 * cambio, y borrar un movimiento haria imposible cuadrar el historial.
 *
 * Los insumos regresan con el costo que se les congelo, no con el vigente. Si
 * regresaran al precio de hoy, revertir y volver a producir cambiaria el valor
 * del inventario sin que hubiera pasado nada real.
 */
export async function revertirProduccion(productionId: string): Promise<void> {
  const produccion = await db.productions.get(productionId);
  if (produccion === undefined) {
    throw new ErrorValidacion('No se encontro la produccion.');
  }
  if (produccion.estado !== 'completada') {
    throw new ErrorValidacion('Solo se puede revertir una produccion completada.');
  }
  if (produccion.revertidaPor !== null) {
    throw new ErrorValidacion('Esta produccion ya se revirtio.');
  }

  // `sales` no indexa productionId, asi que se filtra en memoria. Subir la
  // version del esquema solo por este indice no se justifica: la tabla es
  // pequena por naturaleza y la Fase 5 decidira si vale una migracion.
  const ventas = (await db.sales.toArray()).filter((v) => v.productionId === productionId);
  if (ventas.length > 0) {
    throw new ErrorValidacion(
      'Esta produccion ya tiene ventas registradas. Corrige la venta antes de revertirla.',
    );
  }

  const renglones = await db.productionItems.where('productionId').equals(productionId).toArray();
  const insumos = new Map(
    (await db.ingredients.bulkGet(renglones.map((r) => r.ingredientId)))
      .filter((i): i is Ingredient => i !== undefined)
      .map((i) => [i.id, i]),
  );

  const reversionId = nuevoId('prod');
  const movimientos: ReturnType<typeof construirMovimiento>[] = [];
  const nuevosEstados: Array<{ id: string; estado: ReturnType<typeof estadoDe> }> = [];

  for (const renglon of renglones) {
    const insumo = insumos.get(renglon.ingredientId);
    if (insumo === undefined || renglon.cantidadRealBase <= 0) continue;

    const resultado = aplicarEntradaValuada(
      estadoDe(insumo),
      renglon.cantidadRealBase,
      renglon.costoCentavos,
    );

    nuevosEstados.push({ id: insumo.id, estado: resultado.estado });

    movimientos.push(
      construirMovimiento({
        ingredientId: insumo.id,
        tipo: 'reversion',
        fecha: hoy(),
        deltas: resultado.deltas,
        refTipo: 'production',
        refId: reversionId,
        motivo: `Reversion de la produccion del ${produccion.fechaPrep}`,
      }),
    );
  }

  const reversion: Production = {
    ...produccion,
    id: reversionId,
    estado: 'cancelada',
    porcionesAProducir: -produccion.porcionesAProducir,
    costoTotalCentavos:
      produccion.costoTotalCentavos === null ? null : -produccion.costoTotalCentavos,
    completadaEn: ahoraIso(),
    revertidaPor: null,
    creadoEn: ahoraIso(),
  };

  await enEscritura(
    [db.productions, db.ingredients, db.inventoryMovements],
    async () => {
      await db.productions.add(reversion);
      await db.productions.update(productionId, { revertidaPor: reversionId, estado: 'cancelada' });
      await db.inventoryMovements.bulkAdd(movimientos);
      for (const { id, estado } of nuevosEstados) {
        await db.ingredients.update(id, estado);
      }
    },
  );
}

// ---------------------------------------------------------------------------
// Consultas
// ---------------------------------------------------------------------------

export async function produccionesEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<Production[]> {
  return db.productions.where('fechaPrep').between(desde, hasta, true, true).toArray();
}

export async function obtenerProduccion(id: string): Promise<Production | undefined> {
  return db.productions.get(id);
}

export async function renglonesDeProduccion(productionId: string): Promise<ProductionItem[]> {
  return db.productionItems.where('productionId').equals(productionId).toArray();
}

/** Estado de la produccion del dia siguiente, para avisar con tiempo (Req. 13.11). */
export async function resumenDeManana(fecha: FechaLocal = hoy()): Promise<{
  pendientes: number;
  completadas: number;
}> {
  const agenda = await verAgenda(fecha);
  return {
    pendientes: agenda.prepararHoy.filter((t) => !t.yaCompletada).length,
    completadas: agenda.prepararHoy.filter((t) => t.yaCompletada).length,
  };
}

/** Recetas disponibles para armar el menu. */
export async function recetasParaMenu(): Promise<Recipe[]> {
  return (await db.recipes.toArray())
    .filter((r) => r.activo)
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}
